import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Skeleton } from './ui/skeleton';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Trophy, ArrowUpRight, ArrowDownLeft, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { value: '1h', label: '1H' },
  { value: '6h', label: '6H' },
  { value: '24h', label: '24H' },
  { value: '7d', label: '7D' },
  { value: '30d', label: '30D' },
];

export default function TopTradersTable({ limit = 10 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('24h');
  const [sortBy, setSortBy] = useState('volume');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_URL}/api/analytics/top-traders?period=${period}&limit=${limit}&sort_by=${sortBy}`
        );
        if (!response.ok) throw new Error('Failed to fetch top traders');
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [period, limit, sortBy]);

  const formatAddress = (addr) => {
    if (!addr) return '';
    const known = {
      '0xf28704c691290547924e2129d407da36bda8ce0f': 'Krown Vesting',
      '0x525911d60f3a767b5cf11ea1780d7a095d0075bf': 'KROWN/USDT Pool',
      '0x3a4cbe34a07bd1a3b159edcccd9110c2a98d9a92': 'KROWN/ETH Pool',
      '0xe0ec010899d166559e7a22216659c8da16525c6d': 'SwapRouter02',
    };
    const label = known[addr.toLowerCase()];
    if (label) return label;
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `$${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`;
    return `$${num.toFixed(2)}`;
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <Badge className="bg-yellow-500/20 text-yellow-500">🥇 1st</Badge>;
    if (rank === 2) return <Badge className="bg-gray-400/20 text-gray-400">🥈 2nd</Badge>;
    if (rank === 3) return <Badge className="bg-orange-500/20 text-orange-500">🥉 3rd</Badge>;
    return <Badge variant="outline">#{rank}</Badge>;
  };

  if (loading && !data) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Trophy className="w-5 h-5" />
            Top Traders
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Top Traders</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Failed to load data</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full" data-testid="top-traders-table">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            Top Traders
          </CardTitle>
          <div className="flex gap-1">
            {PERIODS.map(p => (
              <Button
                key={p.value}
                variant={period === p.value ? 'default' : 'ghost'}
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setPeriod(p.value)}
              >
                {p.label}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Sort buttons */}
        <div className="flex gap-2 mb-3">
          <Button
            variant={sortBy === 'volume' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-6 text-xs"
            onClick={() => setSortBy('volume')}
          >
            By Volume
          </Button>
          <Button
            variant={sortBy === 'trades' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-6 text-xs"
            onClick={() => setSortBy('trades')}
          >
            By Trades
          </Button>
          <Button
            variant={sortBy === 'pnl' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-6 text-xs"
            onClick={() => setSortBy('pnl')}
          >
            By P&L
          </Button>
        </div>

        {/* Traders list */}
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {data?.traders?.map((trader) => (
            <div
              key={trader.address}
              className="flex items-center gap-3 p-2 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
            >
              <div className="w-16 shrink-0">
                {getRankBadge(trader.rank)}
              </div>
              
              <div className="flex-1 min-w-0">
                <Link 
                  to={`/address/${trader.address}`}
                  className="text-sm font-mono hover:text-primary flex items-center gap-1"
                >
                  {formatAddress(trader.address)}
                  <ExternalLink className="w-3 h-3" />
                </Link>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                  <span className="flex items-center gap-1">
                    <ArrowDownLeft className="w-3 h-3 text-emerald-500" />
                    {trader.buy_count}
                  </span>
                  <span className="flex items-center gap-1">
                    <ArrowUpRight className="w-3 h-3 text-red-500" />
                    {trader.sell_count}
                  </span>
                  <span>{trader.total_trades} total</span>
                </div>
              </div>

              <div className="text-right shrink-0">
                <div className="text-sm font-bold">
                  {formatNumber(trader.total_volume_usd)}
                </div>
                <div className={`text-xs ${trader.total_pnl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                  {trader.total_pnl >= 0 ? '+' : ''}{formatNumber(trader.total_pnl)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {(!data?.traders || data.traders.length === 0) && (
          <div className="text-center text-sm text-muted-foreground py-8">
            No trading activity in this period
          </div>
        )}
      </CardContent>
    </Card>
  );
}
